/**
 * Klient for meldinger på et anonymt avvik. Levert kontrakt (dabih-api #4690):
 *
 *   GET  /organizations/{orgId}/issues/{issueId}/messages   → IssueMessage[]
 *   POST /organizations/{orgId}/issues/{issueId}/messages   body { "body": "…" }
 *
 * Backend avgjør selv om kalleren er melder eller behandler (reporterRef vs
 * issue-tilgang), setter `author`, og sender det innholdsløse «ny melding»-varselet
 * til riktig motpart. Frontend sender derfor verken rolle eller mottaker.
 * POST gir 409 når avviket er ferdigbehandlet (`Avsluttede`).
 *
 * Bruker den genererte API-klienten (`backend.issues`) — endepunktene ble levert i
 * dabih-api #4690 og finnes nå i den vendrede klienten.
 */
import { ResponseError, type IssueMessageViewModel } from '@trinnvis/dabih-api-client';
import { createClient } from 'src/store/backend';
import type { IssueMessage } from './issue-message.js';

/** Kastes når avviket er ferdigbehandlet og stengt for nye meldinger (HTTP 409). */
export class IssueClosedError extends Error {}

function toIssueMessage(vm: IssueMessageViewModel): IssueMessage {
  return {
    id: vm.id ?? '',
    author: vm.author === 'handler' ? 'handler' : 'reporter',
    authorName: vm.authorName,
    body: vm.body ?? '',
    createdAt: vm.createdAt ?? '',
  };
}

/** Henter meldingstråden on-demand (meldinger er ekskludert fra issue-payloads). */
export async function fetchIssueMessages(organizationId: string, issueId: string): Promise<IssueMessage[]> {
  const messages = await createClient().issues.listIssueMessages({ organizationId, issueId });
  return messages.map(toIssueMessage);
}

/**
 * Sender en melding. Backend setter author + varsler motpart. Den genererte
 * kontrakten returnerer ikke meldingen, så kalleren bør re-hente tråden etterpå.
 */
export async function postIssueMessage(organizationId: string, issueId: string, body: string): Promise<void> {
  try {
    await createClient().issues.createIssueMessage({ organizationId, issueId, issueMessageCommand: { body } });
  } catch (e) {
    if (e instanceof ResponseError && e.response.status === 409) {
      throw new IssueClosedError('Avviket er ferdigbehandlet og stengt for nye meldinger.');
    }
    throw e;
  }
}
